import { effect, Signal, untracked } from "@angular/core";
import { SbCreativeType } from "@front/m19-api-client";
import { LandingPage } from "@front/m19-services";
import { SbPreModerationService } from "@m19-board/strategies/sb-pre-moderation/sb-pre-moderation.service";
import { SbWizardForm } from "./sb-wizard-form";
import {
  assetsAfterBrandOrTypeChange,
  creativesSbAsinsDefaultFormModel,
  formatDefaultFormModel,
} from "./sb-wizard-form.defaults";
import {
  REACHED_AFTER_BRAND,
  REACHED_AFTER_CREATIVE_TYPE,
  REACHED_AFTER_LANDING_PAGE,
  REACHED_AFTER_STORE,
  SbWizardFormModel,
} from "./sb-wizard-types";
import { FormNavigationService } from "@m19-board/shared/form-navigation/form-navigation.service";

interface CascadeSnapshot {
  brandEntityId: string | null;
  sbCreativeType: SbCreativeType;
  sbLandingPage: LandingPage;
  storePageId: string | null;
}

export function bindSbWizardCascade(options: {
  ready: Signal<boolean>;
  creationForm: Signal<SbWizardForm>;
  isVendor: Signal<boolean>;
  formNavigation: FormNavigationService<SbWizardFormModel>;
  preModeration: SbPreModerationService;
}): void {
  let formSnapshot: CascadeSnapshot | null = null;

  effect(() => {
    const form = options.creationForm();
    const ready = options.ready();
    const brand = form.setup.brand().value();
    const creativeType = form.format.sbCreativeType().value();
    const landingPage = form.format.sbLandingPage().value();
    const store = form.format.store().value();
    const current: CascadeSnapshot = {
      brandEntityId: brand?.brandEntityId ?? null,
      sbCreativeType: creativeType,
      sbLandingPage: landingPage,
      storePageId: store?.storePageId ?? null,
    };

    untracked(() => {
      if (!ready) {
        formSnapshot = null;
        return;
      }

      const previous = formSnapshot;
      if (!previous) {
        formSnapshot = current;
        return;
      }

      if (previous.brandEntityId !== current.brandEntityId) {
        form.format().reset(formatDefaultFormModel);
        form.assets().reset(assetsAfterBrandOrTypeChange(brand, options.isVendor()));
        form.creativesSbAsins().reset(creativesSbAsinsDefaultFormModel);
        options.preModeration.reset();
        options.formNavigation.updateReachedStepIds([...REACHED_AFTER_BRAND]);
        formSnapshot = {
          brandEntityId: current.brandEntityId,
          sbCreativeType: formatDefaultFormModel.sbCreativeType,
          sbLandingPage: formatDefaultFormModel.sbLandingPage,
          storePageId: null,
        };
        return;
      }

      if (previous.sbCreativeType !== current.sbCreativeType) {
        form.assets().reset(assetsAfterBrandOrTypeChange(brand, options.isVendor()));
        form.creativesSbAsins().reset(creativesSbAsinsDefaultFormModel);
        options.preModeration.reset();
        options.formNavigation.updateReachedStepIds([...REACHED_AFTER_CREATIVE_TYPE]);
        formSnapshot = current;
        return;
      }

      if (previous.sbLandingPage !== current.sbLandingPage) {
        form.creativesSbAsins().reset(creativesSbAsinsDefaultFormModel);
        options.formNavigation.updateReachedStepIds([...REACHED_AFTER_LANDING_PAGE]);
        formSnapshot = current;
        return;
      }

      if (previous.storePageId !== current.storePageId) {
        form.creativesSbAsins().reset(creativesSbAsinsDefaultFormModel);
        options.formNavigation.updateReachedStepIds([...REACHED_AFTER_STORE]);
        formSnapshot = current;
        return;
      }

      formSnapshot = current;
    });
  });
}
